import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Alert,
} from '@mui/material';
import { getMeasurementTypes, createSingleMeasurement } from '../../services/metrics.service';
import { MeasurementType, CreateMeasurementPayload } from '../../types/metrics';

interface AddMeasurementDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddMeasurementDialog: React.FC<AddMeasurementDialogProps> = ({ open, onClose, onSuccess }) => {
  const [types, setTypes] = useState<MeasurementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [value, setValue] = useState<string>('');
  const [measuredAt, setMeasuredAt] = useState(new Date().toISOString().slice(0, 16));

  const selectedType = types.find(t => t.id === Number(selectedTypeId));

  useEffect(() => {
    if (open) {
      const fetchTypes = async () => {
        try {
          setLoading(true);
          setError(null);
          const data = await getMeasurementTypes();
          setTypes(data);
        } catch (err) {
          setError('Не вдалося завантажити типи вимірювань.');
        } finally {
          setLoading(false);
        }
      };
      fetchTypes();
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedType || !value) {
      setError('Будь ласка, заповніть всі обов\'язкові поля.');
      return;
    }

    const payload: CreateMeasurementPayload = {
      measurementTypeId: Number(selectedTypeId),
      unitId: selectedType.defaultUnit.id,
      value: Number(value),
      measuredAt: new Date(measuredAt).toISOString(),
    };

    try {
      setSubmitting(true);
      setError(null);
      await createSingleMeasurement(payload);
      onSuccess();
      handleClose();
    } catch (err) {
      setError('Помилка при додаванні вимірювання.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedTypeId('');
    setValue('');
    setMeasuredAt(new Date().toISOString().slice(0, 16));
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Додати нове вимірювання</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ my: 4 }}><CircularProgress /></Box>
        ) : (
          <Box component="form" sx={{ mt: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="type-select-label">Тип вимірювання</InputLabel>
              <Select
                labelId="type-select-label"
                value={selectedTypeId}
                label="Тип вимірювання"
                onChange={(e) => setSelectedTypeId(e.target.value)}
              >
                {types.map(type => (
                  <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={`Значення (${selectedType?.defaultUnit.symbol || ''})`}
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              sx={{ mb: 2 }}
              disabled={!selectedType}
            />

            <TextField
              fullWidth
              label="Дата та час вимірювання"
              type="datetime-local"
              value={measuredAt}
              onChange={(e) => setMeasuredAt(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Скасувати</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || submitting}>
          {submitting ? <CircularProgress size={24} /> : 'Додати'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
